import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorMessage, Loading, withValidPermission } from '@Components';
import { useFetchData } from '@CustomHooks';
import { Permission } from '@Models';
import { bopsApi } from '@Network';
import { assignFieldServiceColors } from './assignFieldServiceColor';
import FieldServiceDetail from './fieldServiceDetail';

interface FieldServiceDetailContainerProps {
  fieldServiceId: string;
  retailer?: string;
}

const FieldServiceDetailContainer = ({ fieldServiceId, retailer }: FieldServiceDetailContainerProps) => {
  const { t } = useTranslation();

  const fetchDetail = useCallback(
    () => bopsApi.getFieldServiceDetail(fieldServiceId, retailer),
    [fieldServiceId, retailer],
  );
  const { data: detail, isLoading: isLoadingDetail, error: detailError } = useFetchData(fetchDetail);

  const { data: roster, isLoading: isLoadingRoster } = useFetchData(bopsApi.getFieldServices);

  const colorsById = useMemo(() => {
    if (!detail) return {};
    return assignFieldServiceColors((roster ?? [detail.field_service]).map((fs) => fs.id));
  }, [roster, detail]);

  if (isLoadingDetail || isLoadingRoster) return <Loading title={t('FieldService.LoadingDetail')} />;

  if (detailError || !detail) {
    return <ErrorMessage internalMessage={`Unable to get field service: ${detailError || 'not found'}`} />;
  }

  return <FieldServiceDetail detail={detail} color={colorsById[fieldServiceId]} />;
};

// Same gate as the list container — the detail route must never be reachable when the list isn't.
export default withValidPermission(FieldServiceDetailContainer, [
  Permission.ViewFieldServices,
  Permission.ManageFieldServices,
]);
